import i18n from "lib/i18n"

export enum NWEASubject {
  MATH = "Mathematics",
  READING = "Reading",
  LANGUAGE = "Language Usage",
  SCIENCE = "Science",
  SP_READING = "Reading (Spanish)",
}

export enum NWEAFluencyType {
  ORAL = "Adaptive Oral Reading",
  SCREENER = "Dyslexia Screener",
}

export enum Term {
  FALL = "Fall",
  WINTER = "Winter",
  SPRING = "Spring",
}

export interface RowInput {
  TermName: string
  Grade: string
  Subject: string
  TestRITScore: number
  TestName: string
}

export const normalizeSubject = (subject: string | undefined, course: string | undefined) => {
  if (!subject) throw new Error("Subject is empty")
  if (subject.includes("Reading")) return NWEASubject.READING
  if (subject.includes("Mathematics")) return NWEASubject.MATH
  if (subject.includes("Language")) {
    if (course === "Reading (Spanish)") return NWEASubject.SP_READING
    else if (course === "Reading") return NWEASubject.READING
    else return NWEASubject.LANGUAGE
  }
  if (subject.includes("Science")) return NWEASubject.SCIENCE
  throw new Error(`Unknown subject: ${subject}`)
}
export const normalizeGrade = (grade: string | undefined) =>
  grade === "PK" ? -1 : grade === "K" ? 0 : Number(grade)
export const normalizeTerm = (term: string) => {
  if (term.includes("Spring")) return Term.SPRING
  if (term.includes("Fall")) return Term.FALL
  if (term.includes("Winter")) return Term.WINTER
  throw new Error(`Unknown term: ${term}`)
}

export const getTestName = (testName: string) => {
  let normTestName = testName.replace(/^Growth: /, "")
  normTestName = normTestName.replace(/\s*\+.*$/, (match) => (match.startsWith(" ") ? match : " +"))
  const parts = normTestName.split(":")
  normTestName = parts[0].trim()
  return normTestName
}

export const updateDisplayGrade = (grade) => {
  if (grade == "0" || grade == "K") return "Kindergarten"
  if (grade == "-1" || grade == "PK") return "Pre-Kindergarten"

  const number = parseInt(grade)
  const lastTwoDigits = number % 100

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `th`
  }

  switch (number % 10) {
    case 1:
      return `st`
    case 2:
      return `nd`
    case 3:
      return `rd`
    default:
      return `th`
  }
}

export const getCorrespondingGrowth = (growth) => {
  switch (growth) {
    case "Low":
      return i18n.t("growth.slow", { defaultValue: "Slow" })
    case "LoAvg":
      return i18n.t("growth.slowAvg", { defaultValue: "SlowAvg" })
    case "Avg":
      return i18n.t("growth.avg", { defaultValue: "Avg" })
    case "HiAvg":
      return i18n.t("growth.fastAvg", { defaultValue: "FastAvg" })
    case "High":
      return i18n.t("growth.fast", { defaultValue: "Fast" })
  }
}

export const getCorrespondingRelation = (relation) => {
  switch (relation) {
    case "Low":
      return "less"
    case "LoAvg":
      return "less"
    case "Avg":
      return "the same"
    case "HiAvg":
      return "more"
    case "High":
      return "more"
  }
}

export const readingFluencyActivity = [
  {
    activity: "guessing_game",
    label: "Guessing Game",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_pa024_guessing_game.pdf",
    category: "Phonological Awareness",
    level: 1,
  },
  {
    activity: "feed_the_animals",
    label: "Feed The Animals",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_pa017_feed_the_animals.pdf",
    category: "Phonological Awareness",
    level: 1,
  },
  {
    activity: "rhyming_a_lot_oh",
    label: "Rhyming A Lot Oh",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_pa003_rhyming_a_lot_oh.pdf",
    category: "Phonological Awareness",
    level: 2,
  },
  {
    activity: "phoneme_position_sort",
    label: "Phoneme Position Sort",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/23_pa022_phoneme_position_sort.pdf",
    category: "Phonological Awareness",
    level: 2,
  },
  {
    activity: "whats_left",
    label: "What's left",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/23_pa019_whats_left.pdf",
    category: "Phonological Awareness",
    level: 3,
  },
  {
    activity: "the_phoneme_game",
    label: "The Phoneme Game",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/23_pa015_the_phoneme_game.pdf",
    category: "Phonological Awareness",
    level: 3,
  },
  {
    activity: "final_phoneme_pyramid",
    label: "Final Phoneme Pyramid",
    url:
      "https://homeschoolboss.com/wp-content/uploads/2023/10/23_pa001_initial_phoneme_picture_sort.pdf",
    category: "Phonological Awareness",
    level: 4,
  },
  {
    activity: "initial_phoneme_picture_sort",
    label: "Initial Phoneme Picture Sort",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_pa024_guessing_game.pdf",
    category: "Phonological Awareness",
    level: 4,
  },
  {
    activity: "word_construction",
    label: "Word Construction",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/23_p044_word_construction.pdf",
    category: "Phonics",
    level: 5,
  },
  {
    activity: "syllable_snake",
    label: "Syllable Snake",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/23_p031_syllable_snake.pdf",
    category: "Phonics",
    level: 5,
  },
  {
    activity: "map_a_word",
    label: "Map a Word",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/23_p006_map_a_word.pdf",
    category: "Phonics",
    level: 4,
  },
  {
    activity: "spell_and_sort",
    label: "Spell and Sort",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/23_p020_spell_and_sort.pdf",
    category: "Phonics",
    level: 4,
  },
  {
    activity: "word_steps",
    label: "Word Steps",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_p035_word_steps.pdf",
    category: "Phonics",
    level: 3,
  },
  {
    activity: "vowel_stars",
    label: "Vowel Stars",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_p034_vowel_stars.pdf",
    category: "Phonics",
    level: 3,
  },
  {
    activity: "letter_sound_match",
    label: "Letter Sound Match",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_p025_letter_sound_match.pdf",
    category: "Phonics",
    level: 2,
  },
  {
    activity: "medial_phoneme_spin",
    label: "Medial Phoneme Spin",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_p023_medial_phoneme_spin.pdf",
    category: "Phonics",
    level: 2,
  },
  {
    activity: "letter_sound_dominoes",
    label: "Letter Sound Dominoes",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_p016_letter_sound_dominoes.pdf",
    category: "Phonics",
    level: 1,
  },
  {
    activity: "alphabet_memory_game",
    label: "Alphabet Memory Game",
    url: "https://homeschoolboss.com/wp-content/uploads/2023/10/k1_p008_alphabet_memory_game.pdf",
    category: "Phonics",
    level: 1,
  },
]
